// CS Import — one archived commonsku project: its documents, artwork and
// history, with the historical client alongside (prototype design). The
// prototype's present-day owner field is left out on purpose; no link to any live Flow client and no follow-on actions — reference only.
import { useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { CS_DOC_LABEL, csDay, csMoney, type CsProject } from "@/lib/csArchive";
import { CsDocumentViewer } from "./CsDocumentViewer";
import { CsNotReady, Crumbs, Fact, FileLine, MissingNote, SideCard } from "./parts";

export function CsImportProjectPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const docId = params.get("doc");
  const tab = params.get("tab") ?? "documents";
  const q = useQuery({ queryKey: ["cs-archive", "project", id], queryFn: () => api<CsProject>(`/cs-archive/projects/${id}`) });
  const setParam = useCallback((k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    v ? next.set(k, v) : next.delete(k);
    setParams(next, { replace: k === "tab" });
  }, [params, setParams]);

  if (q.data && !q.data.ready) return <CsNotReady />;
  if (q.isError) return <p className="mt-10 text-[16px] text-red-700">This archived project couldn't be found.</p>;
  if (!q.data) return <p className="mt-10 text-muted">Loading…</p>;
  const { project: p, documents, artwork, files, events } = q.data;
  const otherFiles = files.filter((f) => !artwork.some((a) => a.file?.id === f.id));

  return (
    <div className="mx-auto w-full max-w-[1400px] px-1 pb-16">
      <Crumbs> / <Link to={`/cs-import/clients/${p.client_id}`} className="hover:text-ink">{p.client_name}</Link> / #{p.job_number}</Crumbs>
      <Link to="/cs-import" className="mt-5 inline-block text-[14px] text-muted hover:text-ink">← Back to search results</Link>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-muted">{p.client_name} · #{p.job_number}</p>
          <h1 className="mt-2 text-[40px] font-semibold leading-[1.08] tracking-[-1.2px] text-ink">{p.name}</h1>
          <p className="mt-2 text-[16px] text-muted">{csDay(p.last_activity_on ?? p.created_on)}{p.latest_stage ? ` · ${p.latest_stage}` : ""} · Imported from commonsku</p>
        </div>
        <span className="rounded-md bg-canvas px-3 py-1.5 text-[14px] text-ink">Historical record</span>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
        <Tabs value={tab} onValueChange={(v) => setParam("tab", v === "documents" ? null : v)}>
          <TabsList>
            <TabsTrigger value="documents" className="text-[16px]">Documents <span className="ml-1 text-muted">{documents.length}</span></TabsTrigger>
            <TabsTrigger value="artwork" className="text-[16px]">Artwork & files <span className="ml-1 text-muted">{artwork.length + otherFiles.length}</span></TabsTrigger>
            <TabsTrigger value="history" className="text-[16px]">History <span className="ml-1 text-muted">{events.length}</span></TabsTrigger>
          </TabsList>

          <TabsContent value="documents">
            <ul>
              {documents.map((d) => (
                <li key={d.id} className="flex items-center gap-4 border-b border-rule py-5">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-canvas text-[12px] font-semibold text-muted">{d.original_state === "stored" ? "PDF" : "DATA"}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[17px] font-semibold text-ink">{CS_DOC_LABEL[d.doc_type] ?? d.doc_type} {d.number}</p>
                    <p className="mt-0.5 text-[14px] text-muted">
                      {[csDay(d.doc_date), d.status, d.supplier_name, d.total != null ? csMoney(d.total, d.currency ?? "USD") : null].filter((x) => x && x !== "—").join(" · ")}
                    </p>
                  </div>
                  {d.original_state !== "stored" && d.doc_type !== "bill" ? <MissingNote>{d.original_state === "pending" ? "Original not saved yet" : "Original not available"}</MissingNote> : null}
                  <button type="button" onClick={() => setParam("doc", d.id)} className="inline-flex h-11 shrink-0 items-center rounded-md border border-rule bg-white px-4 text-[15px] font-medium text-ink hover:bg-canvas">Open document ↗</button>
                </li>
              ))}
            </ul>
            {!documents.length ? <p className="py-8 text-muted">commonsku had no documents on this project.</p> : null}
          </TabsContent>

          <TabsContent value="artwork">
            {artwork.length ? <h3 className="mt-5 text-[15px] font-semibold uppercase tracking-[0.08em] text-muted">Artwork as used on this project</h3> : null}
            <ul>
              {artwork.map((a) => a.file ? (
                <FileLine key={a.id} f={a.file} detail={<>{[a.item_name, a.location, a.imprint, a.color, a.size].filter(Boolean).join(" · ")}{a.document_number ? <> · on <button type="button" className="text-ink underline-offset-2 hover:underline" onClick={() => setParam("doc", a.document_id ?? null)}>{CS_DOC_LABEL[a.doc_type ?? ""] ?? "document"} {a.document_number}</button></> : null}</>} />
              ) : (
                <li key={a.id} className="flex items-center justify-between gap-4 border-b border-rule py-4 text-[15px]">
                  <span className="text-ink">{[a.title, a.item_name, a.location].filter(Boolean).join(" · ")}</span><MissingNote>No file in commonsku</MissingNote>
                </li>
              ))}
            </ul>
            {otherFiles.length ? <h3 className="mt-8 text-[15px] font-semibold uppercase tracking-[0.08em] text-muted">Other project files</h3> : null}
            <ul>{otherFiles.map((f) => <FileLine key={f.id} f={f} />)}</ul>
            <p className="mt-5 text-[14px] text-muted">Approval isn't assumed from the newest file — check the document's approval evidence or the History tab.</p>
            {!artwork.length && !otherFiles.length ? <p className="py-8 text-muted">No artwork or files were stored on this project in commonsku.</p> : null}
          </TabsContent>

          <TabsContent value="history">
            <ol className="mt-4 border-l border-rule pl-6">
              {events.map((e) => (
                <li key={e.id} className="relative pb-6">
                  <span className="absolute -left-[29px] top-1.5 h-2.5 w-2.5 rounded-full bg-rule" aria-hidden />
                  <p className="text-[13px] text-muted">{e.occurred_at ? csDay(e.occurred_at) : "—"}{e.actor_name ? ` · ${e.actor_name}` : ""}{e.document_type ? ` · ${e.document_type.replace(/^PO#.*/, "Purchase order")} ${e.document_number ?? ""}` : ""}{e.kind ? ` · ${e.kind.toLowerCase()}` : ""}</p>
                  <p className="mt-1 whitespace-pre-line text-[15px] text-ink">{(e.text ?? "").slice(0, 1200)}</p>
                </li>
              ))}
              <li className="relative"><span className="absolute -left-[29px] top-1.5 h-2.5 w-2.5 rounded-full bg-ink" aria-hidden /><p className="text-[15px] font-semibold text-ink">Available in CS Import</p><p className="text-[14px] text-muted">Read-only · no live pipeline, finance or production activity created</p></li>
            </ol>
            {!events.length ? <p className="text-muted">commonsku recorded no activity on this project.</p> : null}
          </TabsContent>
        </Tabs>

        <div className="space-y-6 lg:pt-14">
          <SideCard title="The client connection.">
            <Fact label="Client">{p.client_name}</Fact>
            <Fact label="Contact">{p.contact_name}</Fact>
            <Fact label="Historical rep">{p.client_rep_name}</Fact>
            {p.order_rep_name && p.order_rep_name !== p.client_rep_name ? <Fact label="Order rep">{p.order_rep_name}</Fact> : null}
            <Fact label="Project">#{p.job_number}</Fact>
            <Link to={`/cs-import/clients/${p.client_id}`} className="mt-4 flex h-11 items-center justify-center rounded-md border border-rule text-[15px] font-medium text-ink hover:bg-canvas">
              All {p.client_name} history ↗
            </Link>
          </SideCard>
        </div>
      </div>

      {docId ? <CsDocumentViewer id={docId} onOpen={(next) => setParam("doc", next)} onClose={() => setParam("doc", null)} /> : null}
    </div>
  );
}
